import { computed, effect, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AppBootstrapStore, AppContext } from '@umss/shared/core';
import { AuthService, UserRoles } from '@umss/shared/data-access';

const ALLOWED_ROLES = [UserRoles.SuperAdmin, UserRoles.BusinessAdministrator];

@Injectable({ providedIn: 'root' })
export class BusinessContextService implements AppContext {
  private authService = inject(AuthService);
  private router = inject(Router);
  private bootstrap = inject(AppBootstrapStore);

  /**
   * Returns true only when the logged-in user has a Business-level role.
   * Used by refreshAccount to gate app bootstrap.
   */
  readonly ready = computed(() => {
    const role = this.authService.authState().role;
    return role !== null && ALLOWED_ROLES.includes(role);
  });

  constructor() {
    // If an existing session is detected with a non-business role (e.g. a
    // Facility user navigating directly to the business app), clear it
    // immediately and redirect to login without waiting for the bootstrap timeout.
    // Gate behind bootstrap.ready so this only fires AFTER refreshAccount() completes,
    // preventing a race condition where role is still null during token refresh.
    effect(() => {
      if (!this.bootstrap.ready()) return;

      const isAuthenticated = this.authService.isAuthenticated();
      const hasCorrectRole = this.ready();

      if (isAuthenticated && !hasCorrectRole) {
        this.authService.closeSession();
        this.router.navigateByUrl('/auth/login');
      }
    });
  }

  /**
   * The business app does not load any per-user context object during bootstrap —
   * `ready` is derived synchronously from the role claim. Resolve immediately.
   */
  ensureLoaded(): Promise<void> {
    return Promise.resolve();
  }
}
